// Auto-logout utility
// Logs out user after inactivity based on role
// Admin: 15 minutes | Regular users: 30 minutes
// Shows warning 2 minutes before logout

// TESTING MODE: Set to true for quick testing, false for production
const TESTING_MODE = false;

// Timeout durations based on user role
const USER_INACTIVITY_TIMEOUT = TESTING_MODE ? 30 * 1000 : 30 * 60 * 1000; // 30 seconds or 30 minutes
const ADMIN_INACTIVITY_TIMEOUT = TESTING_MODE ? 15 * 1000 : 15 * 60 * 1000; // 15 seconds or 15 minutes
const WARNING_TIME = TESTING_MODE ? 10 * 1000 : 2 * 60 * 1000; // 10 seconds or 2 minutes

let INACTIVITY_TIMEOUT = USER_INACTIVITY_TIMEOUT; // Default to user timeout
let WARNING_THRESHOLD = INACTIVITY_TIMEOUT - WARNING_TIME;

let inactivityTimer = null;
let warningTimer = null;
let warningShown = false;

// Events that count as user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

// Callbacks
let onWarning = null;
let onLogout = null;

const clearTimers = () => {
  console.log('Clearing timers - inactivity:', inactivityTimer, 'warning:', warningTimer);
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  warningShown = false;
  console.log('Timers cleared, warningShown reset to false');
};

const performLogout = () => {
  console.log('Performing logout');
  clearTimers();
  
  // Clear authentication data
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('role');
  localStorage.removeItem('lastActivity'); // Clear last activity timestamp
  
  // Call logout callback if provided
  if (onLogout && typeof onLogout === 'function') {
    onLogout();
  } else {
    // Default: redirect to login with message
    window.location.href = '/login?reason=inactivity';
  }
};

const showWarning = () => {
  console.log('Showing warning');
  warningShown = true;
  
  // Clear the warning timer since it already fired
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  
  // Call warning callback if provided
  if (onWarning && typeof onWarning === 'function') {
    onWarning();
  }
  
  // Set final logout timer
  console.log('Setting logout timer for', WARNING_TIME, 'ms');
  inactivityTimer = setTimeout(performLogout, WARNING_TIME);
};

const resetTimer = () => {
  console.log('Resetting timer - clearing existing timers first');
  clearTimers();
  
  // Update last activity timestamp
  localStorage.setItem('lastActivity', Date.now().toString());
  
  console.log('Setting new warning timer for', WARNING_THRESHOLD, 'ms');
  // Set warning timer - this is the ONLY timer we set
  warningTimer = setTimeout(showWarning, WARNING_THRESHOLD);
};

const handleActivity = () => {
  // Only reset if user is still logged in
  const token = localStorage.getItem('token');
  if (!token) {
    return; // Don't stop, just ignore activity
  }
  
  resetTimer();
};

// Throttle activity events to avoid too many resets
let lastActivity = 0;
const THROTTLE_DELAY = 1000; // 1 second

const throttledHandleActivity = () => {
  const now = Date.now();
  if (now - lastActivity > THROTTLE_DELAY) {
    lastActivity = now;
    handleActivity();
  }
};

const start = (options = {}) => {
  // Set callbacks
  onWarning = options.onWarning || null;
  onLogout = options.onLogout || null;
  
  // Only start if user is logged in
  const token = localStorage.getItem('token');
  if (!token) {
    return;
  }

  // Check if user is admin and set appropriate timeout
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  INACTIVITY_TIMEOUT = isAdmin ? ADMIN_INACTIVITY_TIMEOUT : USER_INACTIVITY_TIMEOUT;
  WARNING_THRESHOLD = INACTIVITY_TIMEOUT - WARNING_TIME;
  
  console.log(`Auto-logout initialized for ${isAdmin ? 'ADMIN' : 'USER'} - Timeout: ${INACTIVITY_TIMEOUT / 1000}s, Warning at: ${WARNING_THRESHOLD / 1000}s`);
  
  // Check if session expired while browser was closed
  const lastActivity = localStorage.getItem('lastActivity');
  if (lastActivity) {
    const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
    if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
      console.log('Session expired during browser closure');
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('role');
      localStorage.removeItem('lastActivity');
      
      // Call onLogout callback if provided (this will handle smart routing)
      if (onLogout && typeof onLogout === 'function') {
        onLogout();
      }
      return;
    }
  }
  
  // Add event listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, throttledHandleActivity, true);
  });
  
  // Start the timer
  resetTimer();
};

const stop = () => {
  // Remove event listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, throttledHandleActivity, true);
  });
  
  // Clear timers
  clearTimers();
  
  // Reset callbacks
  onWarning = null;
  onLogout = null;
};

const extendSession = () => {
  console.log('extendSession called - warningShown:', warningShown);
  // Clear timers and reset warning state
  clearTimers();
  // Start fresh timer cycle
  resetTimer();
  console.log('Session extended successfully');
  return true;
};

const autoLogout = {
  start,
  stop,
  extendSession,
  resetTimer
};

export default autoLogout;
